import { useEffect, useRef } from 'react';
import logoSrc from '../assets/logo_1000ad.png';

const SIZES = { sm: 40, md: 64, lg: 96, xl: 128 };

/**
 * 1000AD Logo — renders the official uploaded PNG asset.
 * Always preserves brand colors (black "10∞" + blue "AD").
 * On dark backgrounds, a soft white drop-shadow lifts the dark parts for contrast.
 * @param {'sm'|'md'|'lg'|'xl'} size
 * @param {'auto'|'dark'|'light'} theme  dark = add glow for visibility, light = slight contrast boost
 * @param {boolean} shimmer  one-time shimmer sweep on mount (default false)
 */
export default function Logo({ size = 'md', theme = 'auto', shimmer = false, className = '' }) {
  const h = SIZES[size] || SIZES.md;
  const useDark = theme === 'dark' || theme === 'auto';
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!shimmer) return;
    const el = wrapRef.current;
    if (!el) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    el.classList.add('logo-shimmer-once');
    const t = setTimeout(() => el.classList.remove('logo-shimmer-once'), 1600);
    return () => clearTimeout(t);
  }, [shimmer]);

  /* Dark bg: glow behind logo to lift the black "10∞" without erasing blue "AD" */
  const imgStyle = useDark
    ? {
        height: h,
        width: 'auto',
        filter: 'drop-shadow(0 0 6px rgba(255,255,255,0.5)) drop-shadow(0 0 18px rgba(139,92,246,0.3))',
      }
    : {
        height: h,
        width: 'auto',
        filter: 'contrast(1.05) saturate(1.1)',
      };

  return (
    <span
      ref={wrapRef}
      className={`logo-shimmer-wrap inline-block ${className}`}
      style={{ lineHeight: 0 }}
    >
      <img
        src={logoSrc}
        alt="1000AD"
        data-testid="logo-wordmark"
        width={Math.round(h * 3.2)}
        height={h}
        className="inline-block object-contain select-none"
        style={imgStyle}
        draggable={false}
      />
    </span>
  );
}
